import { useEffect, useState } from 'react';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { useField } from 'formik';
import { getCustomStyles } from '@/theme/components/select';
import { Option } from '@/components/select/Option';
import { createLabelForSelect } from '../../SelectComponents';
import { fetchSelectOptions } from '../../helpers/formHelpers';
import { useCreatableSelectHelpers } from './creatableSelectHelpers';
import debounce from 'debounce-promise';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';

export const AsyncCreatableSelectField = ({ item, formik = {} }) => {
  const [selected, setSelected] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [optionsL, setOptionsL] = useState([]);
  const {
    name = '',
    isMulti = false,
    useInput = false,
    fetchConfig = {},
    placeholder = '',
    createPlaceHolder = 'Create',
    menuPlacement = 'auto',
    disabled,
    options = [],
    newStyles = [],
    defaultValue,
    valueKey = 'value',
    labelKey = 'label',
    components = {},
    isAppendToBody = false,
    isClearable = false,
    styleVersion = 'v1',
    validationType = '',
    customLabelSelect,
  } = item;

  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { error, initialValue, value } = meta;

  const { submitCount } = formik || {};
  const showError = submitCount > 0 && error;

  const styles = KSelectStylesConfig({
    variant: 'default',
    customStyles: newStyles,
  });

  const styleVersionMap = {
    v1: getCustomStyles(newStyles),
    v2: styles,
  };

  const selectStyles = styleVersionMap[styleVersion] || styles;

  const {
    onChange,
    onCreate,
    formatOptionLabel,
    isValidNewOption,
    NoOptionsMessageComp,
  } = useCreatableSelectHelpers({
    item,
    setSelected,
    valueKey,
    labelKey,
    setIsLoading,
  });

  useEffect(() => {
    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      setSelected(null);
    }
  }, [value]);

  useEffect(() => {
    let newOptions;

    if (validationType === 'array') {
      newOptions = options.filter((item) =>
        meta.value?.includes?.(item?.[valueKey])
      );
    } else if (Array.isArray(initialValue) && initialValue?.length > 0) {
      newOptions = initialValue.map((value) => ({ label: value, value }));
    } else if (initialValue?.length > 0) {
      newOptions = { label: initialValue, value: initialValue };
    }
    setSelected(newOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);
  useEffect(() => {
    if (!defaultValue?.id) return;
    setSelected({
      label: defaultValue?.name,
      value: defaultValue?.id,
    });
  }, [defaultValue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedLoadOptions = debounce(async (input, cb) => {
    const endPoint = useInput && input && fetchConfig?.endPoint + input;
    if (!endPoint) return;

    //todo: hiding old code - this is used when using user input to search the api
    // const endPoint = useInput
    //   ? fetchConfig?.endPoint + (input || 'a')
    //   : fetchConfig?.endPoint;

    const response = await fetchSelectOptions({
      ...fetchConfig,
      endPoint,
    });
    cb(response);
    setOptionsL(response);
    return response;
  }, 300);

  if (isAppendToBody) {
    return (
      <AsyncCreatableSelect
        classNamePrefix="asyncCreateableField"
        styles={selectStyles}
        isMulti={isMulti}
        inputId={name}
        placeholder={
          isLoading
            ? 'Creating...'
            : placeholder || 'Search or create an option...'
        }
        loadOptions={debouncedLoadOptions}
        options={options}
        hasError={showError}
        menuPortalTarget={document.body}
        components={{
          Option,
          IndicatorSeparator: null,
          NoOptionsMessage: NoOptionsMessageComp,
          ...components,
        }}
        formatOptionLabel={formatOptionLabel}
        isValidNewOption={isValidNewOption}
        onChange={onChange}
        onCreateOption={onCreate}
        menuPlacement={menuPlacement}
        value={selected}
        isClearable={isClearable}
        isDisabled={disabled || isLoading}
        isLoading={isLoading}
        formatCreateLabel={
          customLabelSelect ||
          ((val) => createLabelForSelect(val, createPlaceHolder, optionsL))
        }
        defaultOptions={true}
      />
    );
  }

  return (
    <>
      <AsyncCreatableSelect
        styles={selectStyles}
        isMulti={isMulti}
        inputId={name}
        placeholder={
          isLoading
            ? 'Creating...'
            : placeholder || 'Search or create an option...'
        }
        loadOptions={debouncedLoadOptions}
        className="kula_custom_select__container"
        classNamePrefix="kula_custom_select"
        options={options}
        hasError={showError}
        components={{
          Option,
          IndicatorSeparator: null,
          NoOptionsMessage: NoOptionsMessageComp,
          ...components,
        }}
        formatOptionLabel={formatOptionLabel}
        isValidNewOption={isValidNewOption}
        onChange={onChange}
        onCreateOption={onCreate}
        menuPlacement={menuPlacement}
        value={selected}
        isDisabled={disabled || isLoading}
        isLoading={isLoading}
        isClearable={isClearable}
        formatCreateLabel={
          customLabelSelect ||
          ((val) => createLabelForSelect(val, createPlaceHolder, optionsL))
        }
        defaultOptions={true}
      />
    </>
  );
};
